import React from "react";
import { PhoneCall, MapPin, Star, Clock, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

import { companyInfo, workingHours, contact, benefits } from "@/mock/zebraCarsMock";

export const HeroSection = () => {
  const handleCallClick = () => {
    window.location.href = `tel:${contact.phone}`;
  };

  const handleMapClick = () => {
    window.open(contact.googleMapsUrl, "_blank", "noopener,noreferrer");
  };

  const handleCopyAddress = () => {
    if (navigator.clipboard) {
      navigator.clipboard.
      writeText(contact.address).
      then(() => toast.success("Adresa kopirana")).
      catch(() => toast.error("Nije uspelo kopiranje"));
    } else {
      toast("Adresa: " + contact.address);
    }
  };

  return (
    <section className="hero-section relative overflow-hidden !bg-[#303030]">
      <div className="relative z-10 max-w-[87.5rem] lg:px-10 flex flex-col lg:flex-row items-center justify-between mx-auto px-5 gap-12 py-20 !bg-[#313131]">
        <div className="hero-content max-w-xl !bg-[#303030]">
          <h1 className="hero-title mb-6 text-[var(--text-primary)]">{companyInfo.name}</h1>
          <p className="body-large mb-4 text-[var(--text-primary)]">{companyInfo.subtitle}</p>
          <p className="body-medium mb-8 !opacity-[0%] text-[var(--text-secondary)]">{companyInfo.description}</p>

          <div className="flex flex-wrap items-center gap-4 mb-8">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[var(--text-primary)]" />
              <span className="body-medium text-[var(--text-primary)]">
                {companyInfo.googleRating.toString().replace(".", ",")} Google ocena
              </span>
            </div>
            <Badge className="border border-[var(--border-light)] bg-white/5 text-[var(--text-primary)] px-3 py-1 rounded-full">
              Brza registracija bez čekanja
            </Badge>
          </div>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleCallClick}
              className="btn-primary btn-hover-scale inline-flex items-center gap-2">
              <PhoneCall className="w-4 h-4" />
              Pozovite nas
            </button>
            <button
              onClick={handleMapClick}
              className="btn-secondary btn-hover-scale inline-flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Pronađi nas na mapi
            </button>
            <a
              href="https://superregistracija.azurewebsites.net/zakazitehnickiframe.aspx?t=CY8zN2LOYwz1UAJRs2ctA9XoTuoVWDNbR7XmpHeDJmRwyLbNXJ0paHdk6Jz8oOwVsvRrMeb1%2byAd2yKUDTaMiC8RxlffDn%2b%2f%2by8BvGauJCg%3d"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex"
            >
              <button className="btn-primary btn-hover-scale inline-flex items-center gap-2">
                ZAKAŽI TEHNIČKI PREGLED
              </button>
            </a>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-white/90">
            {benefits.map((benefit) =>
            <div
              key={benefit}
              className="flex items-center gap-2 text-sm border border-[var(--border-light)] rounded-full px-3 py-2 bg-white/5">

                <ChevronRight className="w-4 h-4 text-[var(--text-primary)]" />
                <span>{benefit}</span>
              </div>
            )}
          </div>
        </div>

        <div className="w-full max-w-md space-y-5">
          <Card className="bg-[var(--bg-card)] border-[var(--border-light)] text-[var(--text-primary)]">
            <CardHeader className="flex flex-col space-y-1.5 p-6 !bg-[#202020]">
              <CardTitle className="flex items-center justify-between text-base font-semibold">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[var(--text-primary)]" />
                  Radno vreme
                </span>
                <span className="text-xs uppercase tracking-[0.12em] text-white/60">
                  Bez zakazivanja
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 p-6 pt-0 !bg-[#202020]">
              {workingHours.map((item) =>
              <div key={item.day} className="flex items-center justify-between text-sm">
                  <span className="text-white/80">{item.day}</span>
                  <span className="font-medium text-[var(--text-primary)]">{item.hours}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[var(--bg-card)] border-[var(--border-light)] text-[var(--text-primary)]">
            <CardHeader className="flex flex-col space-y-1.5 p-6 !bg-[#1E1E1E]">
              <CardTitle className="flex items-center justify-between text-base font-semibold">
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[var(--text-primary)]" />
                  Kako do nas
                </span>
                <span className="text-xs text-white/60">Novi Sad, Klisa</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 !text-sm p-6 pt-0 !bg-[#303030]">
              <p className="!leading-[5rem]">{contact.address}</p>
              <div className="flex flex-wrap gap-3">
                <Button
                  size="sm"
                  className="btn-primary rounded-full px-4 py-2"
                  onClick={handleMapClick}>

                  Navigacija
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="btn-secondary rounded-full px-4 py-2"
                  onClick={handleCopyAddress}>

                  Kopiraj adresu
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="creative-ribbon-back hidden md:flex bg-black border-t border-b border-[var(--border-light)] text-[var(--text-primary)] pointer-events-none -z-10">
        Bez čekanja · Bez šetanja · Sve na jednom mestu
      </div>
      <div className="creative-ribbon hidden md:flex bg-[var(--bg-card)] text-[var(--text-primary)] pointer-events-none -z-10">
        Registracija vozila · Tehnički pregled · Osiguranje
      </div>
    </section>);

};

export default HeroSection;
